
import React, { createContext, useState, useEffect } from 'react';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  hasConsented: boolean;
  preferences: CookiePreferences;
  showBanner: boolean;
  showPreferences: boolean;
  acceptAll: () => void;
  denyAll: () => void;
  allowSelection: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  updatePreferences: (prefs: Partial<CookiePreferences>) => void;
  canUse: (type: 'analytics' | 'marketing') => boolean;
}

export const CookieConsentContext = createContext<CookieConsentContextType | null>(null);



export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true and non-toggleable
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check for existing consent
    const storedConsent = localStorage.getItem('gfy_cookie_consent');
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('gfy_consent='));

    if (storedConsent && consentCookie) {
      const consentData = JSON.parse(storedConsent);
      setPreferences(consentData.preferences);
      setHasConsented(true);
      setShowBanner(false);
    } else {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    const consentData = {
      preferences: prefs,
      timestamp: new Date().toISOString(),
    };

    // Store in localStorage
    localStorage.setItem('gfy_cookie_consent', JSON.stringify(consentData));

    // Set cookie with 12 month expiry
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 12);
    document.cookie = `gfy_consent=true; expires=${expiryDate.toUTCString()}; path=/`;

    setPreferences(prefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const denyAll = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const allowSelection = () => {
    saveConsent(preferences);
  };

  const openPreferences = () => {
    setShowPreferences(true);
  };

  const closePreferences = () => {
    setShowPreferences(false);
  };

  const updatePreferences = (prefs: Partial<CookiePreferences>) => {
    setPreferences(prev => ({
      ...prev,
      ...prefs,
      essential: true, // Always keep essential as true
    }));
  };

  const canUse = (type: 'analytics' | 'marketing'): boolean => {
    return hasConsented && preferences[type];
  };

  const contextValue: CookieConsentContextType = {
    hasConsented,
    preferences,
    showBanner,
    showPreferences,
    acceptAll,
    denyAll,
    allowSelection,
    openPreferences,
    closePreferences,
    updatePreferences,
    canUse,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};
